import { PageHeader, LoadingDots } from '@flos/react-ui';
import { type WizardPageComponent } from '../../config/wizardPagesConfig';
import { useHippoText } from '../../hooks/useHippoText';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import usePageNavigation from '../../hooks/usePageNavigation';
import { useRecoilState } from 'recoil';
import { mpsStatusAtom } from '../../recoil/atoms/mpsStatusAtom';
import { mpsActivationInfoAtom } from '../../recoil/atoms/mpsActivationInfoAtom';
import { useEffect } from 'react';
import { useAwaitGetMpsStatus } from '../../hooks/useAwaitGetMpsStatus';
import RejectedStatusVariant from './components/RejectedStatusVariant';

const MPSAgreementStatusPage: WizardPageComponent = () => {
    const getHippoText = useHippoText();
    const { wizardGotoStep } = useWizardNavigation();
    const { awaitGetMpsStatus } = useAwaitGetMpsStatus();
    const { goToAIAFlow, goToReceiptPage } = usePageNavigation();
    const searchParams = new URLSearchParams(location.search);
    const [MPSAgreementStatusInfo, setMPSAgreementStatusInfo] = useRecoilState(mpsStatusAtom);
    const agreementStatus = MPSAgreementStatusInfo.status;
    const [mpsActivationInfo, setMpsActivationInfo] = useRecoilState(mpsActivationInfoAtom);

    const PendingStatusVariant = (): React.JSX.Element => {
        return (
            <PageHeader
                title={getHippoText('DSS-MPS-FLOW-AGREEMENT-STATUS-PENDING-SECTION-HEADER')}
                align="center"
                subtitle={getHippoText('DSS-MPS-FLOW-AGREEMENT-STATUS-PENDING-SECTION-SUBHEADER')}
                headArea={<LoadingDots />}
            ></PageHeader>
        );
    };

    const getMpsStatusInBackground = async (): Promise<void> => {
        const response = await awaitGetMpsStatus();
        const latestStatus = response?.status ?? 'Pending';
        setMPSAgreementStatusInfo((prev) => {
            return {
                ...prev,
                status: latestStatus
            };
        });
        if (latestStatus === 'Active') {
            mpsActivationInfo.isSourceFromAIA ? goToAIAFlow() : goToReceiptPage();
        } else if (latestStatus === 'Expired' || latestStatus === 'New') {
            handleBackToMpsActivationPage();
        }
    };

    const handleBackToMpsActivationPage = (): void => {
        searchParams.delete('confirmedToPay');
        const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
        window.history.replaceState({}, '', newUrl);
        setMpsActivationInfo((prevState) => ({
            ...prevState,
            isConfirmedToPay: false
        }));
        wizardGotoStep('MPSActivationPage');
    };

    useEffect(() => {
        void getMpsStatusInBackground();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (agreementStatus === 'Rejected')
        return (
            <RejectedStatusVariant handleBackToMpsActivationPage={handleBackToMpsActivationPage} />
        );
    return <PendingStatusVariant />;
};

export default MPSAgreementStatusPage;
